import axios, { AxiosError } from 'axios';
import { useState, useMemo } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';
import {
  createRestApi,
  DefaultGetAllResponse,
  DefaultGetResponse,
} from '~/App/Api/rest';
import { MessageTemplate } from '~/types/models';

const baseUrl = '/api/v3/message-templates';

export const queryKeys = {
  all: ['message-templates'],
  lists: () => [...queryKeys.all, 'lists'] as const,
  list: (query?: string) => [...queryKeys.lists(), query] as const,
  details: () => [...queryKeys.all, 'detail'] as const,
  detail: (uuid: string) => [...queryKeys.details(), uuid] as const,
};
export interface MessageTemplatesRequestOptions {
  limit?: number;
  query?: string;
}

export interface UseMessageTemplatesProps {
  onTemplatesLoaded?: (result: DefaultGetAllResponse<MessageTemplate>) => void;
}

const useMessageTemplates = ({
  onTemplatesLoaded,
}: UseMessageTemplatesProps) => {
  const [query, setQuery] = useState<string>();

  const api = useMemo(() => createRestApi<MessageTemplate>(baseUrl), []);

  async function getMessageTemplates({
    limit = 50,
    query,
  }: MessageTemplatesRequestOptions) {
    const filters: null | Record<string, string | Array<string | null>> = null;

    return api.getAll({ limit, query, filters, with_demo: 0 });
  }

  const {
    isLoading,
    isRefetching,
    data: messageTemplates,
  } = useQuery(
    queryKeys.list(query),
    () => getMessageTemplates({ query: query ?? undefined }),
    {
      onSuccess: (result) => {
        if (onTemplatesLoaded) {
          onTemplatesLoaded(result);
        }
      },
    }
  );

  messageTemplates?.data;
  return {
    setQuery,
    isLoading,
    isRefetching,
    messageTemplates,
  };
};

export interface FetchMessageTemplateArgs {
  templateUuid: string;
}

export interface FetchMessageTemplateResponse {
  data: MessageTemplate;
}

export async function fetchMessageTemplate({
  templateUuid,
}: FetchMessageTemplateArgs) {
  const res = await axios.get<FetchMessageTemplateResponse>(
    `${baseUrl}/${templateUuid}`
  );
  return res.data;
}
export interface UseMessageTemplateHookParams extends FetchMessageTemplateArgs {
  options?: UseQueryOptions<
    DefaultGetResponse<FetchMessageTemplateResponse>,
    AxiosError
  >;
}

export const useMessageTemplate = ({
  templateUuid,
  options,
}: UseMessageTemplateHookParams) =>
  useQuery<DefaultGetResponse<FetchMessageTemplateResponse>, AxiosError>(
    queryKeys.detail(templateUuid),
    () => fetchMessageTemplate({ templateUuid }),
    options
  );

export default useMessageTemplates;
